import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    linkedSignal,
    signal,
    untracked,
    viewChild,
} from "@angular/core";
import { Bind } from "../../decorators/bind.decorator";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25LoadingInlineStaticComponent } from "../../modules/s25-loading/s25.loading.inline.static.component";
import type { OptBean } from "../../modules/s25-opt/s25.opt.component";
import { OptUtil } from "../../modules/s25-opt/s25.opt.util";
import { S25OptModule } from "../../modules/s25-opt/s25.opt.module";
import type { OptUtilI } from "../../modules/s25-opt/s25.opt.util";
import { S25FavoriteModule } from "../../modules/s25-favorite/s25.favorite.module";
import { S25TableComponent } from "../../modules/s25-table/s25.table.component";
import { S25TableModule } from "../../modules/s25-table/s25.table.module";
import type { Table } from "../../modules/s25-table/Table";
import { ListService } from "../../services/list.service";
import { NetworkHealthService } from "../../services/network.health.service";
import { AngBridgeUtil } from "../../util/ang.bridge.util";
import { S25Const } from "../../util/s25-const";
import { ListTableHelper } from "./list.table.helper";
import type { ListColumn, ListResponse } from "./list.table.types";
import type { ListConfig } from "./list.config";
import { S25ItemReservationListConfig } from "./item.reservation.list.config";
import { PublisherListConfig } from "./publisher.list.config";
import { s25Resource } from "../../util/s25.resource";
import { S25IconButtonComponent } from "../s25.icon.button.component";
import { Item } from "../../pojo/Item";

export type ListType =
    | { kind: "home" }
    | { kind: "reservation"; itemTypeId: Item.Ids; itemId: number; startDate?: string | Date | null }
    | { kind: "publisherAdmin"; publisherBean: PublisherListConfig.AdminBean };

const homeListConfig: ListConfig = {
    ...S25ItemReservationListConfig.sharedBehavior,
    caption: "Reservation List",
    listData: {
        compSubject: "rm_rsrv",
        compType: "homelist",
        defaultSort: "event",
        defaultOrder: "asc",
    },
    hostId: "homeList",
    errorMessageLabel: "Reservation List",
    loadOptBean: (refreshTable) => OptUtil.formHomeListBean(refreshTable, {}),
};

export function optListConfigForType(listType: ListType): ListConfig {
    if (listType.kind === "publisherAdmin") {
        return PublisherListConfig.forType(listType);
    }

    if (listType.kind === "home") {
        return homeListConfig;
    }

    return S25ItemReservationListConfig.forType(listType);
}

@TypeManagerDecorator("s25-ng-list-opt")
@Component({
    selector: "s25-ng-list-opt",
    host: {
        "[class.reservation-list]": "!isPublisherList()",
        "[attr.id]": "config().hostId ?? null",
    },
    template: `
        @if (sourceOptBean.resource.isLoading()) {
            <s25-ng-loading-inline-static />
        }

        @if (optBean()) {
            <s25-ng-opt
                [modelBean]="optBean()!"
                (modelValueChange)="updateOptBean($event)"
                (endDateChange)="updateOptBean()"
                (daysChange)="updateOptBean()"
                (refreshed)="updateOptBean()"
                (queryChange)="handleRequestParamsChange()"
                (initialized)="onOptBarInitialized()"
            />

            @if (tableReady()) {
                <s25-ng-table
                    [caption]="config().caption"
                    [columnSortable]="true"
                    [dataSource]="tableData"
                    [stickyColumnSortPreference]="stickyColumnSortPreference()"
                    [unlimitedWidth]="true"
                    [pivotThreshold]="0"
                    [hasTotalRowCount]="true"
                    [minColWidth]="50"
                />
            }
        }

        @if (errorMessage()) {
            <div class="NoInformation">
                <span>
                    @if (!sourceOptBean.resource.isLoading()) {
                        <s25-ng-icon-button [type]="'refresh'" [ariaLabel]="'Retry'" (clicked)="retry($event)" />
                    }
                    {{ errorMessage() }}
                </span>
            </div>
        }
    `,
    styles: `
        s25-ng-loading-inline-static {
            display: block;
            margin: auto;
            width: fit-content;
        }

        :host ::ng-deep .s25-ng-table--cell.stateTentative {
            font-style: italic;
        }

        :host ::ng-deep .s25-ng-table--cell.stateCancelled {
            color: var(--conflict-color);
            font-weight: 400;
        }

        :host ::ng-deep .s25-ng-table--cell.stateRequested_changes {
            color: var(--color--requested-changes);
            font-weight: 400;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25OptModule, S25TableModule, S25FavoriteModule, S25LoadingInlineStaticComponent, S25IconButtonComponent],
})
export class ListWithOptions {
    readonly listType = input.required<ListType>();

    readonly table = viewChild(S25TableComponent);

    readonly config = computed(() => optListConfigForType(this.listType()));

    readonly helper = new ListTableHelper(this.table, this.config);

    readonly inApp = S25Const.inApp;

    protected readonly isHomeList = computed(() => this.listType().kind === "home");
    protected readonly isPublisherList = computed(() => this.listType().kind === "publisherAdmin");

    protected readonly stickyColumnSortPreference = computed(() => {
        // home list piggy-backs off rm_rsrv, but we don't want to use those sort preferences
        if (this.listType().kind === "home") {
            return undefined;
        }

        return this.config().listData.compSubject;
    });

    private readonly optBarEverInitialized = signal(false);
    protected readonly optBarInitialized = computed(
        () => this.optBarEverInitialized() && !this.sourceOptBean.resource.isLoading() && !!this.sourceOptBean.value(),
    );
    protected readonly tableReady = computed(() => {
        if (!this.optBarInitialized() || this.sourceOptBean.resource.error()) {
            return false;
        }

        if (this.isPublisherList()) {
            const publisherListType = this.listType();
            return publisherListType.kind === "publisherAdmin" && !!publisherListType.publisherBean;
        }

        return !!this.optBean()?.date && !!this.optBean()?.endDate;
    });
    protected readonly sourceOptBean = s25Resource({
        params: () => ({
            config: this.config(),
            optBeanRunTime: this.config().optBeanRunTime(this.listType()),
        }),
        loader: ({ params }): Promise<OptUtilI> =>
            params.config
                .loadOptBean(() => this.handleRequestParamsChange(), params.optBeanRunTime)
                .catch((caughtError) => {
                    if (this.inApp) {
                        const label = params.config.errorMessageLabel;
                        const message = !untracked(NetworkHealthService.healthy)
                            ? `Unable to load ${label} due to poor network conditions. Please check your connection and try again.`
                            : `Unable to load ${label}. Please try again later.`;
                        throw new Error(message, { cause: caughtError });
                    }
                    throw caughtError;
                }),
    });

    readonly optBean = linkedSignal<OptBean | undefined>(() => this.sourceOptBean.value());
    readonly errorMessage = computed(() => this.sourceOptBean.resource.error()?.message);
    /**
     * When request parameters change, abort pending requests (which are now "out of scope")
     */
    private requestParamsScope = new AbortController();

    /**
     * the table's initial fetch is pending before we override the sort order,
     * so this property intercepts the request in `getRows`
     */
    #shouldTryDefaultSort = true;

    readonly tableData: Table.Paginated = {
        type: "paginated",
        dataSource: this.getRows,
        columns: [],
        defaultPageSize: 25,
        pageSizes: [25, 50, 100],
        customRowCount: (counts) => this.config().formatRowCount(counts),
    };

    constructor() {
        AngBridgeUtil.init(this);

        /*
         * TODO: ideally there would be an input on table component. instead, we manually
         *  set the column sort on init
         */
        effect(() => {
            const table = this.table();
            if (table) {
                const listData = untracked(this.config).listData;
                if (table.sortedColumnId) {
                    this.#shouldTryDefaultSort = false;
                } else {
                    table.sortedColumnId = ListService.ListSortColMap[listData.defaultSort] ?? listData.defaultSort;
                    table.sortedColumnOrder = listData.defaultOrder;
                    table.changeDetector.markForCheck();
                }
            } else {
                this.#shouldTryDefaultSort = true;
            }
        });
    }

    protected onOptBarInitialized(): void {
        this.optBarEverInitialized.set(true);
    }

    updateOptBean(optBean?: OptBean): void {
        const sourceBean = optBean ?? this.optBean();
        this.optBean.set(sourceBean ? { ...sourceBean } : undefined);
    }

    handleRequestParamsChange(): void {
        this.requestParamsScope.abort("Request parameters have changed");
        this.requestParamsScope = new AbortController();
        this.helper.refreshTable(true);
    }

    retry(event?: Event): void {
        event?.stopPropagation();
        this.sourceOptBean.resource.reload();
    }

    @Bind
    async getRows(tableQuery: Table.PaginatedQuery): Promise<Table.DataSourceResponse | undefined> {
        const data = await this.fetchListData(tableQuery);
        if (!data) {
            return;
        }

        const rows = data.rows?.length ? this.helper.mapRows(data.cols, data.rows) : [];

        return {
            rows,
            totalRows: data.rows?.[0]?.count ?? data.count ?? rows.length,
            cacheId: data.cacheId,
        };
    }

    private async fetchListData(tableQuery: Table.PaginatedQuery): Promise<ListResponse | undefined> {
        const optBean = this.optBean();
        if (!optBean) {
            return { cols: [], rows: [] };
        }

        const currentListType = this.listType();
        if (currentListType.kind === "publisherAdmin" && !currentListType.publisherBean) {
            return { cols: [], rows: [] };
        }

        const listConfig = this.config();
        const abortSignal = this.requestParamsScope.signal;
        try {
            if (this.#shouldTryDefaultSort) {
                this.#shouldTryDefaultSort = false;
                // if the tableQuery did not load a sort column from preferences
                if (!tableQuery.sortColumn?.id) {
                    tableQuery = this.withDefaultSort(tableQuery);
                }
            }
            const data = await listConfig.fetchListData({
                config: listConfig,
                listType: this.listType(),
                optBean,
                tableQuery,
                abortSignal,
            });

            this.setColumnsFromData(data);

            if (data.lastupdate) {
                this.optBean.update((bean) => ({
                    ...bean!,
                    lastupdate: data.lastupdate,
                }));
            }
            return data;
        } catch (err) {
            if (!abortSignal.aborted) {
                throw err;
            }
        }
    }

    private withDefaultSort(tableQuery: Table.PaginatedQuery): Table.PaginatedQuery {
        const listData = this.config().listData;
        return {
            ...tableQuery,
            sortColumn: {
                id: listData.defaultSort,
                order: listData.defaultOrder,
            },
        };
    }

    private setColumnsFromData(data: ListResponse): void {
        if (!data.cols?.length) {
            return;
        }

        const listConfig = this.config();
        const listStateId = listConfig.listStateId?.(this.listType());
        const mergedCols = listConfig.mergeColumns?.(data.cols, listStateId) ?? data.cols;
        this.helper.setColumnsFromListColumns(mergedCols);

        const columnChooser = listConfig.columnChooser;
        if (columnChooser) {
            this.setupColumnChooser(mergedCols, columnChooser.listColumnPrefName);
        }
    }

    private setupColumnChooser(cols: ListColumn[], listColumnPrefName?: string): void {
        this.optBean.update((bean) => ({
            ...bean!,
            colChooseBean: {
                colList: cols as never,
                ...(listColumnPrefName ? { listColumnPrefName } : {}),
                updateCallback: () => this.onColumnChooserUpdate(),
            },
        }));

        this.helper.applyColumnVisibility(cols);
    }

    onColumnChooserUpdate(): void {
        if (!this.config().columnChooser) {
            return;
        }

        this.helper.applyColumnVisibility((this.optBean()?.colChooseBean?.colList ?? []) as ListColumn[]);
    }
}
